/**
 * Configuration for available effort capacity by month and work category
 * Values are in "jours-homme" (person-days)
 */

import { WorkCategory } from "../types";

/**
 * Monthly capacity for each work category
 */
export interface MonthlyCapacity {
  [WorkCategory.DEVELOPMENT]: number;
  [WorkCategory.IA]: number;
  [WorkCategory.PROJECT_MANAGEMENT]: number;
  [WorkCategory.CONTENT_CREATION]: number;
  [WorkCategory.COG_SCI]: number;
}

/**
 * Default monthly capacity for all work categories
 * Adjust these values based on your team's availability
 */
export const DEFAULT_MONTHLY_CAPACITY: MonthlyCapacity = {
  [WorkCategory.DEVELOPMENT]: 50,
  [WorkCategory.IA]: 18,
  [WorkCategory.PROJECT_MANAGEMENT]: 10,
  [WorkCategory.CONTENT_CREATION]: 15,
  [WorkCategory.COG_SCI]: 18,
};

const normalizeCapacityValue = (
  value: unknown,
  fallback: number,
): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, parsed);
};

const sanitizeMonthlyCapacity = (
  input: Partial<Record<WorkCategory, unknown>> | null | undefined,
): MonthlyCapacity => ({
  [WorkCategory.DEVELOPMENT]: normalizeCapacityValue(
    input?.[WorkCategory.DEVELOPMENT],
    DEFAULT_MONTHLY_CAPACITY[WorkCategory.DEVELOPMENT],
  ),
  [WorkCategory.IA]: normalizeCapacityValue(
    input?.[WorkCategory.IA],
    DEFAULT_MONTHLY_CAPACITY[WorkCategory.IA],
  ),
  [WorkCategory.PROJECT_MANAGEMENT]: normalizeCapacityValue(
    input?.[WorkCategory.PROJECT_MANAGEMENT],
    DEFAULT_MONTHLY_CAPACITY[WorkCategory.PROJECT_MANAGEMENT],
  ),
  [WorkCategory.CONTENT_CREATION]: normalizeCapacityValue(
    input?.[WorkCategory.CONTENT_CREATION],
    DEFAULT_MONTHLY_CAPACITY[WorkCategory.CONTENT_CREATION],
  ),
  [WorkCategory.COG_SCI]: normalizeCapacityValue(
    input?.[WorkCategory.COG_SCI],
    DEFAULT_MONTHLY_CAPACITY[WorkCategory.COG_SCI],
  ),
});

let configuredMonthlyCapacity: MonthlyCapacity = {
  ...DEFAULT_MONTHLY_CAPACITY,
};

export function getConfiguredMonthlyCapacity(): MonthlyCapacity {
  return { ...configuredMonthlyCapacity };
}

export function setConfiguredMonthlyCapacity(capacity: MonthlyCapacity): void {
  configuredMonthlyCapacity = sanitizeMonthlyCapacity(capacity);
}

export function resetConfiguredMonthlyCapacity(): void {
  configuredMonthlyCapacity = { ...DEFAULT_MONTHLY_CAPACITY };
}

/**
 * Get available capacity for a specific work category in a given month
 * @param workCategory - The work category
 * @param year - Year (for future month-specific overrides)
 * @param month - Month (0-11, for future month-specific overrides)
 * @returns Available capacity in person-days
 */
export function getMonthlyCapacity(
  workCategory: WorkCategory,
  year?: number,
  month?: number
): number {
  // For now, return default capacity
  // In the future, you could add logic for:
  // - Holiday months with reduced capacity
  // - Seasonal variations
  // - Specific month overrides
  return configuredMonthlyCapacity[workCategory];
}

/**
 * Get total available capacity for all work categories in a month
 * @param year - Year (optional)
 * @param month - Month (0-11, optional)
 * @returns Total available capacity across all categories
 */
export function getTotalMonthlyCapacity(year?: number, month?: number): number {
  return Object.values(WorkCategory).reduce((total, category) => {
    return total + getMonthlyCapacity(category, year, month);
  }, 0);
}

/**
 * Check if planned effort exceeds available capacity
 * @param plannedEffort - Planned effort for the month
 * @param workCategory - Work category
 * @param year - Year (optional)
 * @param month - Month (0-11, optional)
 * @returns True if over capacity
 */
export function isOverCapacity(
  plannedEffort: number,
  workCategory: WorkCategory,
  year?: number,
  month?: number
): boolean {
  const capacity = getMonthlyCapacity(workCategory, year, month);
  return plannedEffort > capacity;
}

/**
 * Calculate capacity utilization percentage
 * @param plannedEffort - Planned effort for the month
 * @param workCategory - Work category
 * @param year - Year (optional)
 * @param month - Month (0-11, optional)
 * @returns Utilization percentage (e.g., 150 means 150% utilized)
 */
export function getCapacityUtilization(
  plannedEffort: number,
  workCategory: WorkCategory,
  year?: number,
  month?: number
): number {
  const capacity = getMonthlyCapacity(workCategory, year, month);
  if (capacity === 0) return 0;
  return (plannedEffort / capacity) * 100;
}
